/** I11 as a check, not a promise.
 *
 * "Saves store simulation state and commands, never presentation state" is only
 * an invariant if something refuses a file that breaks it. Two mechanisms do:
 *
 *  1. **Closed key sets** on the envelope and on the state root. A `GameState`
 *     has exactly eighteen keys; a file with a nineteenth is refused whatever
 *     it is called. This is also how I13 bites: add a field to `GameState` and
 *     every committed fixture stops validating until a migration adds it too.
 *  2. **Closed key sets on the shapes below the root** - `shapes.ts`. The root
 *     alone was not enough: `PlayerState.townlessDays` proved that a nested
 *     field can be added with nothing at all failing, leaving a loaded save
 *     holding `undefined` where the type promises a number.
 *  3. **A deny-list sweep** for the words presentation state travels under -
 *     camera, selection, animation, texture - which catches a field nobody has
 *     declared anywhere, including inside the free-form records below.
 *
 * The sweep skips the handful of records whose keys are *data*: an object's
 * payload, a fog memory snapshot, a town's dwelling counts. Their keys come
 * from content, and a content author who names a field `cameraShake` is a
 * content review problem, not a save format problem.
 */

import type { GameState } from "../state.js";
import type { PersistError } from "./types.js";
import { findShapeIssues } from "./shapes.js";
import { SAVE_KIND, REPLAY_KIND, malformedFile } from "./types.js";
import { TA_TAG_KEY } from "../hash/canonical.js";
import type { Result } from "../result.js";
import { Err, Ok } from "../result.js";

// ------------------------------------------------------- the closed key sets

/** Section 4's root shape, byte-sorted. Extending it requires an ADR *and* a
 *  migration; this array is the thing that makes forgetting either one fail. */
export const GAME_STATE_KEYS = [
  "armies", "battle", "caravans", "contentVersion", "counters", "fog", "formatVersion",
  "heroes", "map", "objects", "pending", "players", "rng", "rootSeed", "towns",
  "turn", "version", "victory",
] as const;

type AssertNever<T extends never> = T;
type MissingKey = Exclude<keyof GameState, (typeof GAME_STATE_KEYS)[number]>;
type ExtraKey = Exclude<(typeof GAME_STATE_KEYS)[number], keyof GameState>;

/**
 * A compile error here means `GameState` changed. That is the point: update the
 * list, then add `migrations/NNNN.ts` and a fixture, or the fixture test fails
 * next (I13).
 */
export type StateKeysAreExhaustive = readonly [AssertNever<MissingKey>, AssertNever<ExtraKey>];

// --------------------------------------------------------- the I11 deny list

/** Exact key names. Lower-cased before the lookup. */
const PRESENTATION_KEYS: ReadonlySet<string> = new Set([
  "camera", "zoom", "pan", "viewport", "scroll", "scrollx", "scrolly",
  "selected", "selection", "selectedhero", "selectedtown", "focus", "focused",
  "hover", "hovered", "highlight", "highlighted",
  "animation", "animations", "animating", "tween", "easing", "elapsed", "frame",
  "sprite", "sprites", "texture", "textures", "atlas", "canvas", "renderer",
  "scene", "stage", "layer", "overlay", "cursor", "tooltip", "dragging",
  "fps", "audio", "volume", "muted", "sound", "music",
  "mouse", "pointer", "keyboard", "theme", "locale", "language",
  "screen", "panel", "dialog", "modal",
]);

/**
 * Substrings, for the compound names the exact list would miss
 * (`cameraTarget`, `heroSprite`, `moveAnimationMs`). Every entry has been
 * checked against the live state shape: none of them appears in it.
 */
const PRESENTATION_SUBSTRINGS: readonly string[] = [
  "camera", "sprite", "texture", "animation", "tween", "viewport",
  "canvas", "pixi", "scroll", "hover", "tooltip", "cursor", "dragging",
];

/**
 * Records whose keys are content data rather than code. Segment `*` matches one
 * key. The subtree below a match is not swept.
 */
const FREE_FORM: readonly (readonly string[])[] = [
  ["state", "objects", "*", "payload"],
  ["state", "fog", "*", "memory"],
  ["state", "towns", "*", "available"],
  ["state", "heroes", "*", "artifacts"],
  ["state", "pending", "resumeToken", "data"],
];

function matchesFreeForm(path: readonly string[]): boolean {
  for (const pat of FREE_FORM) {
    if (pat.length !== path.length) continue;
    let hit = true;
    for (let i = 0; i < pat.length; i++) {
      const seg = pat[i];
      if (seg !== "*" && seg !== path[i]) { hit = false; break; }
    }
    if (hit) return true;
  }
  return false;
}

function presentationHit(key: string): boolean {
  const k = key.toLowerCase();
  if (PRESENTATION_KEYS.has(k)) return true;
  for (const s of PRESENTATION_SUBSTRINGS) if (k.includes(s)) return true;
  return false;
}

/**
 * Sweep a parsed tree for presentation fields. Returns the first offender, so
 * the message names a key and a path rather than a count.
 */
export function findPresentationField(
  v: unknown, path: readonly string[] = [],
): { readonly path: string; readonly key: string } | null {
  if (v === null || typeof v !== "object") return null;
  if (Array.isArray(v)) {
    for (let i = 0; i < v.length; i++) {
      const hit = findPresentationField((v as readonly unknown[])[i], [...path, String(i)]);
      if (hit !== null) return hit;
    }
    return null;
  }
  const o = v as Record<string, unknown>;
  if (Object.prototype.hasOwnProperty.call(o, TA_TAG_KEY)) return null;
  for (const k of Object.keys(o).sort()) {
    if (presentationHit(k)) return { path: path.length === 0 ? "$" : `$.${path.join(".")}`, key: k };
    const childPath = [...path, k];
    if (matchesFreeForm(childPath)) continue;
    const hit = findPresentationField(o[k], childPath);
    if (hit !== null) return hit;
  }
  return null;
}

// ------------------------------------------------------------- validation

function keySetIssues(
  o: Record<string, unknown>, expected: readonly string[], what: string,
): string[] {
  const have = Object.keys(o).sort();
  const want = new Set(expected);
  const issues: string[] = [];
  for (const k of have) if (!want.has(k)) issues.push(`${what}: unexpected key "${k}"`);
  for (const k of expected) {
    if (!Object.prototype.hasOwnProperty.call(o, k)) issues.push(`${what}: missing key "${k}"`);
  }
  return issues;
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === "object" && !Array.isArray(v);

/**
 * Structural validation of a save tree that has already been migrated to the
 * current format. Returns the same tree on success; decoding happens after.
 */
export function validateSaveTree(raw: unknown, formatVersion: number): Result<Record<string, unknown>, PersistError> {
  if (!isRecord(raw)) return Err(malformedFile("the root of a save is an object"));

  const issues = keySetIssues(raw, ["contentVersion", "formatVersion", "kind", "state"], "save");
  if (raw["kind"] !== SAVE_KIND) {
    issues.push(`save: kind is "${String(raw["kind"])}", expected "${SAVE_KIND}"`);
  }
  if (typeof raw["contentVersion"] !== "string") issues.push("save: contentVersion is not a string");
  if (raw["formatVersion"] !== formatVersion) {
    issues.push(`save: formatVersion is ${String(raw["formatVersion"])}, expected ${String(formatVersion)}`);
  }
  const state = raw["state"];
  if (!isRecord(state)) {
    issues.push("save: state is not an object");
    return Err(malformedFile(...issues));
  }
  issues.push(...keySetIssues(state, [...GAME_STATE_KEYS], "state"));

  // The two version stamps travel twice - on the envelope, where a reader looks
  // first, and inside the state, where the kernel reads them. Disagreement means
  // one of them was hand-edited, and there is no way to tell which.
  if (state["formatVersion"] !== formatVersion) {
    issues.push(`state: formatVersion ${String(state["formatVersion"])} disagrees with the envelope`);
  }
  if (state["contentVersion"] !== raw["contentVersion"]) {
    issues.push("state: contentVersion disagrees with the envelope");
  }
  if (typeof state["version"] !== "number" || !Number.isInteger(state["version"])) {
    issues.push("state: version is not an integer");
  }
  if (typeof state["rootSeed"] !== "number" || !Number.isInteger(state["rootSeed"])) {
    issues.push("state: rootSeed is not an integer");
  }
  if (issues.length > 0) return Err(malformedFile(...issues));

  // The presentation sweep runs before the per-shape key sets because it gives
  // the more specific diagnosis: `turn.cameraTarget` is an I11 leak, and saying
  // "unexpected key" about it would be true but unhelpful.
  const leak = findPresentationField(raw, []);
  if (leak !== null) return Err({ kind: "PRESENTATION_FIELD", path: leak.path, key: leak.key });

  // I13 below the root. A save whose players predate `townlessDays` is refused
  // here rather than decoded into a `PlayerState` with an `undefined` field.
  const shape = findShapeIssues(raw);
  if (shape.length > 0) return Err(malformedFile(...shape));

  return Ok(raw);
}

/** The envelope checks a replay shares with a save, plus its own. */
export function validateReplayEnvelope(raw: unknown): Result<Record<string, unknown>, PersistError> {
  if (!isRecord(raw)) return Err(malformedFile("the root of a replay is an object"));
  const issues = keySetIssues(
    raw,
    ["checkpoints", "commands", "contentVersion", "formatVersion", "initial", "kind", "rootSeed"],
    "replay",
  );
  if (raw["kind"] !== REPLAY_KIND) {
    issues.push(`replay: kind is "${String(raw["kind"])}", expected "${REPLAY_KIND}"`);
  }
  if (!Array.isArray(raw["commands"])) issues.push("replay: commands is not a list");
  if (!Array.isArray(raw["checkpoints"])) issues.push("replay: checkpoints is not a list");
  if (typeof raw["rootSeed"] !== "number") issues.push("replay: rootSeed is not a number");
  if (issues.length > 0) return Err(malformedFile(...issues));
  return Ok(raw);
}
